import { create } from 'zustand'
import { patientApi } from '@/api/patientApi'

export const usePatientStore = create((set, get) => ({
  patients: [],
  loading: false,
  search: '',
  limit: 50,
  offset: 0,
  hasMore: false,
  /** patient_id → patient object cache (for detail views) */
  cache: {},

  setSearch: (search) => set({ search, offset: 0 }),

  /** Fetch the patient list. Pass `{ append: true }` to paginate. */
  fetchList: async ({ append = false } = {}) => {
    const { search, limit, offset } = get()
    set({ loading: true })
    try {
      const data = await patientApi.list({ search, limit, offset })
      const arr = Array.isArray(data) ? data : []
      set((s) => ({
        patients: append ? [...s.patients, ...arr] : arr,
        loading: false,
        hasMore: arr.length === limit,
      }))
    } catch {
      set({ loading: false })
    }
  },

  nextPage: () => {
    const { offset, limit, hasMore, loading } = get()
    if (!hasMore || loading) return
    set({ offset: offset + limit })
    return get().fetchList({ append: true })
  },

  resetPagination: () => set({ offset: 0, hasMore: false }),

  /** Cache and refresh a single patient by id (used by detail page). */
  fetchOne: async (id) => {
    try {
      const data = await patientApi.get(id)
      set((s) => ({ cache: { ...s.cache, [id]: data } }))
      return data
    } catch (e) {
      return null
    }
  },

  /** After a successful create — drop into local list at top. */
  prepend: (patient) =>
    set((s) => ({ patients: [patient, ...s.patients] })),

  /** After an update — replace in list and cache. */
  upsert: (patient) =>
    set((s) => ({
      patients: s.patients.map((p) =>
        p.patient_id === patient.patient_id ? patient : p,
      ),
      cache: { ...s.cache, [patient.patient_id]: patient },
    })),

  /** After a delete — remove. */
  remove: (id) =>
    set((s) => ({
      patients: s.patients.filter((p) => p.patient_id !== id),
      cache: Object.fromEntries(
        Object.entries(s.cache).filter(([k]) => k !== id),
      ),
    })),
}))
